import React, { useState } from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { motion, AnimatePresence } from 'framer-motion';

const MODULE_TABS = [
    { id: 'all', label: 'All Connections', icon: 'hub' },
    { id: 'crm', label: 'CRM Ops', icon: 'support_agent' },
    { id: 'qms', label: 'QMS & Quality', icon: 'verified' },
    { id: 'lms', label: 'LMS Academy', icon: 'school' },
    { id: 'oblique_ai', label: 'Oblique AI', icon: 'auto_awesome' },
];

const CONNECTIONS = [
    {
        id: 'google',
        name: 'Google Workspace',
        desc: 'Connect your personal Gmail and Calendar for seamless CRM synchronization.',
        logo: '/Connections/google.svg',
        type: 'oauth',
        modules: ['all', 'crm', 'lms'],
        scopes: [
            { id: 'gmail', label: 'Gmail', subLabel: 'Email Dispatch', logo: '/Connections/gmail.svg', color: '#EA4335' },
            { id: 'calendar', label: 'Calendar', subLabel: 'Event Sync', logo: '/Connections/googlecalendar.svg', color: '#4285F4' },
            { id: 'drive', label: 'Drive', subLabel: 'Atomic Storage', logo: '/Connections/googledrive.svg', color: '#34A853' },
        ],
    },
    {
        id: 'hubspot',
        name: 'HubSpot CRM',
        desc: 'Personal API link for contact sync and deal tracking.',
        logo: '/Connections/hubspot.svg',
        type: 'apikey',
        modules: ['all', 'crm', 'qms'],
        apiLabel: 'Private Access Token',
        apiplaceholder: 'pat-na1-xxxxxxxx-xxxx...',
        apiHint: 'Retrieve from Integrations → Private Apps in HubSpot.',
    },
    {
        id: 'ringcentral',
        name: 'RingCentral',
        desc: 'Link your RingCentral account for click-to-dial and call logging.',
        logo: '/Connections/RingCentral_icon.webp',
        type: 'apikey',
        modules: ['all', 'qms'],
        apiLabel: 'JWT Credentials',
        apiplaceholder: 'eyJhbGci...',
        apiHint: 'Generated in RingCentral Developer Console.',
    },
    {
        id: '3cx',
        name: '3CX Phone System',
        desc: 'Connect your 3CX extension for real-time call telemetry.',
        logo: 'https://www.3cx.com/wp-content/themes/3cx/assets/images/3cx-logo-icon.png',
        type: 'apikey',
        modules: ['all', 'qms'],
        apiLabel: 'Extension Secret',
        apiplaceholder: 'xxxxxxxx-xxxx...',
        apiHint: 'Found in your 3CX Web Client → Settings.',
    },
    {
        id: 'openai',
        name: 'OpenAI (Oblique AI)',
        desc: 'Connect OpenAI to power automated Deal Audits and Transcript Analysis.',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
        type: 'apikey',
        modules: ['all', 'oblique_ai', 'qms'],
        apiLabel: 'OpenAI API Key',
        apiplaceholder: 'sk-proj-xxxxxxxx-xxxx...',
        apiHint: 'Retrieve from platform.openai.com/api-keys.',
    },
    {
        id: 'gemini',
        name: 'Google Gemini AI',
        desc: 'Multimodal audio/video transcription and AI Deal Audits via Google AI Studio.',
        logo: '/Connections/google.svg',
        type: 'apikey',
        modules: ['all', 'oblique_ai', 'qms'],
        apiLabel: 'Gemini API Key',
        apiplaceholder: 'AIzaSy...',
        apiHint: 'Generate your free API key at aistudio.google.com/app/apikey.',
    },
];

const GoogleIcon = () => (
    <svg width="22" height="22" viewBox="0 0 48 48" fill="none">
        <path fill="#4285F4" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.7 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.4 13.1 17.7 9.5 24 9.5z" />
        <path fill="#34A853" d="M46.2 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.5c-.5 2.9-2.1 5.3-4.5 6.9l7 5.4C43.6 37 46.2 31.2 46.2 24.5z" />
        <path fill="#FBBC05" d="M10.5 28.4A14.8 14.8 0 0 1 9.5 24c0-1.5.3-3 .7-4.4l-7.9-6.1A23.9 23.9 0 0 0 0 24c0 3.8.9 7.4 2.6 10.5l7.9-6.1z" />
        <path fill="#EA4335" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7-5.4c-2.1 1.4-4.7 2.2-8.2 2.2-6.3 0-11.6-3.6-13.5-9.1l-7.9 6.1C6.5 43 14.7 48 24 48z" />
    </svg>
);

export default function ConnectionsSettings({ setMessage }) {
    const [activeModule, setActiveModule] = useState('all');
    const [connectedIds, setConnectedIds] = useState(() => {
        const saved = localStorage.getItem('oblique_connected_ids');
        return saved ? JSON.parse(saved) : ['cloudflare'];
    });
    const [showModal, setShowModal] = useState(null);
    const [showGoogleModal, setShowGoogleModal] = useState(false);
    const [apiKeys, setApiKeys] = useState(() => {
        const saved = localStorage.getItem('oblique_api_keys');
        return saved ? JSON.parse(saved) : {};
    });
    const [showKey, setShowKey] = useState({});
    const [selectedScopes, setSelectedScopes] = useState({ gmail: true, calendar: true, drive: true });

    const filteredConnections = CONNECTIONS.filter(conn => conn.modules.includes(activeModule));

    const handleApiSave = (id) => {
        if (!apiKeys[id]?.trim()) {
            setMessage?.({ type: 'error', text: 'Credentials required for protocol activation.' });
            return;
        }
        setConnectedIds(p => {
            const newIds = [...new Set([...p, id])];
            localStorage.setItem('oblique_connected_ids', JSON.stringify(newIds));
            return newIds;
        });
        localStorage.setItem('oblique_api_keys', JSON.stringify(apiKeys));
        setShowModal(null);
        setMessage?.({ type: 'success', text: `${CONNECTIONS.find(c => c.id === id)?.name} link established.` });
    };

    const handleGoogleConnect = () => {
        setConnectedIds(p => {
            const newIds = [...new Set([...p, 'google'])];
            localStorage.setItem('oblique_connected_ids', JSON.stringify(newIds));
            return newIds;
        });
        setShowGoogleModal(false);
        setMessage?.({ type: 'success', text: `Google protocols active.` });
    };

    const disconnect = (id) => { 
        if (!window.confirm(`Sever connection to ${id}?`)) return;
        setConnectedIds(p => {
            const newIds = p.filter(x => x !== id);
            localStorage.setItem('oblique_connected_ids', JSON.stringify(newIds));
            return newIds;
        }); 
        if (apiKeys[id]) {
            setApiKeys(p => {
                const newKeys = { ...p };
                delete newKeys[id];
                localStorage.setItem('oblique_api_keys', JSON.stringify(newKeys));
                return newKeys;
            });
        }
    };

    const activeModal = showModal ? CONNECTIONS.find(c => c.id === showModal) : null;

    return (
        <div className="animate-fade-in">
            {/* Module Switcher — Horizontal */}
            <div style={{ 
                display: 'flex', alignItems: 'center', gap: '2px', 
                marginBottom: '32px', borderBottom: '1px solid var(--border)',
                paddingBottom: '2px'
            }}>
                {MODULE_TABS.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveModule(tab.id)}
                        style={{
                            padding: '10px 16px', borderRadius: '8px 8px 0 0', border: 'none',
                            background: 'transparent', cursor: 'pointer', position: 'relative',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            color: activeModule === tab.id ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            fontSize: '12px', fontWeight: activeModule === tab.id ? '600' : '500',
                            transition: 'all 0.2s'
                        }}
                    >
                        <ObliqueIcon name={tab.icon} size={14} color={activeModule === tab.id ? 'var(--accent)' : 'var(--text-tertiary)'} />
                        {tab.label}
                        {activeModule === tab.id && (
                            <motion.div 
                                layoutId="conn-tab-underline"
                                style={{ position: 'absolute', bottom: -2, left: 0, right: 0, height: '2px', background: 'var(--accent)' }}
                            />
                        )}
                    </button>
                ))}
            </div>

            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px', fontFamily: 'Lexend Deca', letterSpacing: '-0.02em' }}>Module Integrations</h2>
                <p style={{ color: 'var(--text-tertiary)', fontSize: '12px', fontWeight: '500' }}>Orchestrate high-fidelity data flow for {MODULE_TABS.find(t => t.id === activeModule).label}.</p>
            </div>

            <div style={{ display: 'grid', gap: '12px' }}>
                <AnimatePresence mode="wait">
                    {filteredConnections.map((conn, idx) => {
                        const isConnected = connectedIds.includes(conn.id);
                        return (
                            <motion.div 
                                key={conn.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2, delay: idx * 0.05 }}
                                style={{ 
                                    padding: '20px', borderRadius: '16px', border: '1px solid var(--border)',
                                    background: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', gap: '20px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <div style={{ 
                                    width: '48px', height: '48px', borderRadius: '12px', 
                                    background: '#fff', border: '1px solid var(--border)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', flexShrink: 0
                                }}>
                                    <img src={conn.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <h3 style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>{conn.name}</h3>
                                        {isConnected && (
                                            <span style={{ 
                                                fontSize: '9px', fontWeight: '500', textTransform: 'uppercase', 
                                                color: 'var(--success)', background: 'var(--success-subtle)', 
                                                padding: '2px 8px', borderRadius: '6px'
                                            }}>Live Link</span>
                                        )}
                                    </div>
                                    <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: '500', marginTop: '4px' }}>{conn.desc}</p>
                                </div>
                                <button 
                                    onClick={() => isConnected ? disconnect(conn.id) : (conn.id === 'google' ? setShowGoogleModal(true) : setShowModal(conn.id))}
                                    style={{ 
                                        padding: '10px 20px', borderRadius: '12px', border: '1px solid var(--border)',
                                        background: isConnected ? 'var(--bg-surface)' : 'var(--text-primary)',
                                        color: isConnected ? 'var(--text-primary)' : 'var(--bg-surface)',
                                        fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {isConnected ? 'Disconnect' : 'Configure'}
                                </button>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
                {filteredConnections.length === 0 && (
                    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-tertiary)', border: '1px dashed var(--border)', borderRadius: '16px' }}>
                        <ObliqueIcon name="extension" size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                        <p style={{ fontSize: '13px' }}>No specialized integrations for this module yet.</p>
                    </div>
                )}
            </div>

            {/* Credential Modal */}
            {showModal && activeModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ width: '440px', background: 'var(--bg-surface)', padding: '40px', borderRadius: '32px', border: '1px solid var(--border)', boxShadow: '0 40px 100px rgba(0,0,0,0.3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                            <div style={{ width: '48px', height: '48px', background: 'white', border: '1px solid var(--border)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                                <img src={activeModal.logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: '500' }}>Authorize {activeModal.name}</h3>
                                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '500' }}>Credential Handshake</p>
                            </div>
                        </div>
                        <div style={{ marginBottom: '32px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '8px' }}>{activeModal.apiLabel}</label>
                            <input
                                type={showKey[activeModal.id] ? 'text' : 'password'}
                                value={apiKeys[activeModal.id] || ''}
                                onChange={e => setApiKeys(p => ({ ...p, [activeModal.id]: e.target.value }))}
                                style={{ width: '100%', padding: '14px 16px', borderRadius: '14px', border: '1px solid var(--border)', background: 'var(--bg-subtle)', outline: 'none' }}
                                placeholder={activeModal.apiplaceholder}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setShowModal(null)} style={{ flex: 1, padding: '14px', borderRadius: '14px', border: '1px solid var(--border)', background: 'transparent', fontWeight: '500' }}>Cancel</button>
                            <button onClick={() => handleApiSave(activeModal.id)} style={{ flex: 1, padding: '14px', borderRadius: '14px', border: 'none', background: 'var(--text-primary)', color: 'white', fontWeight: '500' }}>Connect</button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Google Modal */}
            {showGoogleModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ width: '480px', background: 'var(--bg-surface)', padding: '40px', borderRadius: '32px', border: '1px solid var(--border)', boxShadow: '0 40px 100px rgba(0,0,0,0.3)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
                            <div style={{ width: '48px', height: '48px', background: 'white', border: '1px solid var(--border)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
                                <GoogleIcon />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '18px', fontWeight: '500' }}>Google Workspace</h3>
                                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: '500' }}>Scope Negotiation</p>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gap: '10px', marginBottom: '32px' }}>
                            {CONNECTIONS.find(c => c.id === 'google').scopes.map(scope => (
                                <div key={scope.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--bg-subtle)', borderRadius: '14px' }}>
                                    <div style={{ width: '24px', height: '24px' }}><img src={scope.logo} alt="" style={{ width: '100%' }} /></div>
                                    <div style={{ flex: 1, fontSize: '13px', fontWeight: '500' }}>{scope.label}</div>
                                    <input type="checkbox" defaultChecked />
                                </div>
                            ))}
                        </div>
                        <button onClick={handleGoogleConnect} style={{ width: '100%', padding: '14px', borderRadius: '14px', border: 'none', background: 'var(--text-primary)', color: 'white', fontWeight: '500' }}>Initialize Link</button>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
